/**
 * /people/:rosterId/affiliations — the registration office's check on an
 * attestation (M43; Brian's rehearsal note #5).
 *
 * Every agency this person has attested to, what they said they were there,
 * and — one press — a request to that agency's representative to confirm it.
 * The representative answers by a link with no account; the answer lands
 * here and in the asker's inbox, threaded under the request.
 *
 * The signed attestation is still the evidence (Decisions #52-#55). This page
 * is the office checking it, recorded beside it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import { useAuth } from '../auth/useAuth.js';
import ErrorBanner from '../components/ErrorBanner.jsx';

const STAMP = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' });
const DAY = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeZone: 'America/New_York' });
const stamp = (iso) => (iso ? STAMP.format(new Date(iso)) : '—');
const day = (iso) => (iso ? DAY.format(new Date(iso)) : null);
const worker = (w) => (w === 'paid' ? 'Paid' : w === 'volunteer' ? 'Volunteer' : w || null);
const SUB_ROLE = { supervisor: 'Supervisor', training_officer: 'Training officer', education_committee: 'Education committee', medical_director: 'Medical director', system_admin: 'System admin' };

function CheckStatus({ check }) {
  if (!check) return <span className="muted">Not asked yet</span>;
  if (check.status === 'confirmed') return <span className="badge badge--published">✓ Confirmed {day(check.responded_at)}</span>;
  if (check.status === 'denied') return <span className="badge badge--cancelled">✖ Not confirmed {day(check.responded_at)}</span>;
  const expired = Date.parse(check.expires_at) < Date.now();
  return (
    <span className="badge badge--warn">
      {expired ? 'Asked — link expired' : `Asked ${day(check.requested_at)} — waiting`}
      {check.mail_status !== 'sent' ? ` (email ${check.mail_status.replace('_', ' ')})` : ''}
    </span>
  );
}

export function AffiliationsPage() {
  const { rosterId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const canAsk = profile?.permissions?.includes('roster.manage') ?? false;
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(null); // agency_name being asked
  const [form, setForm] = useState({ to: '', to_name: '', contact_id: null, note: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch(`/api/v1/affiliations/${rosterId}`));
    } catch (err) {
      setError(err);
      setData(null);
    }
  }, [rosterId]);
  useEffect(() => {
    load();
  }, [load]);

  function startAsk(a) {
    const c = a.contacts?.[0];
    setAsking(a.agency_name);
    setForm({ to: c?.email ?? '', to_name: c?.full_name ?? '', contact_id: c?.id ?? null, note: '' });
    setError(null);
  }

  async function send(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/v1/affiliations/${rosterId}/checks`, {
        method: 'POST',
        body: JSON.stringify({
          agency_name: asking,
          to: form.to.trim(),
          to_name: form.to_name.trim() || null,
          contact_id: form.contact_id,
          note: form.note.trim() || null,
        }),
      });
      setAsking(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (data === undefined) return <p className="muted">Loading…</p>;

  return (
    <>
      <p className="crumb">
        <button type="button" className="btn btn--quiet btn--auto" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </p>
      <h1>Affiliations{data?.person ? ` — ${data.person.full_name}` : ''}</h1>
      <ErrorBanner error={error} />
      {data && (
        <>
          <p className="muted">
            {data.person.identity_status === 'unconfirmed' ? 'Student ID not confirmed yet. ' : `Student ID ${data.person.external_id}. `}
            Every agency this person has attested to, with what they said they were there. The signed attestation is the
            fee-waiver evidence; asking the agency is the office&apos;s check on it, and the answer is recorded here.
          </p>

          <section className="panel" aria-labelledby="aff-heading">
            <h2 id="aff-heading">Agencies attested to</h2>
            {data.affiliations.length === 0 && <p className="muted">No agency on any enrolment.</p>}
            {data.affiliations.length > 0 && (
              <div className="schedule__scroll">
                <table className="schedule__table">
                  <thead>
                    <tr>
                      <th scope="col">Agency</th>
                      <th scope="col">As</th>
                      <th scope="col">Last attested</th>
                      <th scope="col">Confirmation</th>
                      <th scope="col">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.affiliations.map((a) => (
                      <tr key={a.agency_name}>
                        <td>
                          <strong>{a.agency_name}</strong>
                          <div className="muted">
                            {a.enrollments} enrolment{a.enrollments === 1 ? '' : 's'}
                          </div>
                        </td>
                        <td>{[worker(a.worker_type), a.role_title].filter(Boolean).join(', ') || <span className="muted">—</span>}</td>
                        <td>{a.last_attested_at ? day(a.last_attested_at) : <span className="muted">not signed</span>}</td>
                        <td>
                          <CheckStatus check={a.latest_check} />
                          {a.latest_check?.responder_note && <div className="muted">“{a.latest_check.responder_note}”</div>}
                        </td>
                        <td>
                          {canAsk && (
                            <button type="button" className="btn btn--auto" onClick={() => startAsk(a)} disabled={busy}>
                              {a.latest_check ? 'Ask again' : 'Ask the agency'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {asking && (
            <section className="panel" aria-labelledby="ask-heading">
              <h2 id="ask-heading">Ask {asking} to confirm</h2>
              {(() => {
                const a = data.affiliations.find((x) => x.agency_name === asking);
                return (
                  <>
                    <p className="muted">
                      They will see: <em>{data.person.full_name} attested that they are affiliated with {asking}
                      {[worker(a?.worker_type), a?.role_title].filter(Boolean).length ? ` as: ${[worker(a?.worker_type), a?.role_title].filter(Boolean).join(', ')}` : ''}
                      .</em> Not the student ID. They answer with one tap; replies to the email come to you.
                    </p>
                    {a?.contacts?.length > 0 ? (
                      <label className="field">
                        <span>Representative on file</span>
                        <select
                          value={form.contact_id ?? ''}
                          onChange={(e) => {
                            const c = a.contacts.find((x) => x.id === e.target.value);
                            setForm({ ...form, contact_id: c?.id ?? null, to: c?.email ?? '', to_name: c?.full_name ?? '' });
                          }}
                        >
                          {a.contacts.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.full_name} — {SUB_ROLE[c.sub_role] ?? c.sub_role} — {c.email}
                            </option>
                          ))}
                          <option value="">Someone else (type below)</option>
                        </select>
                      </label>
                    ) : (
                      <p className="callout callout--warn">
                        <strong>No representative on file for {asking}.</strong>
                        <span className="callout__detail">
                          Type their address below. To keep one on file, add a supervisor for this agency under Compliance → Setup.
                        </span>
                      </p>
                    )}
                  </>
                );
              })()}
              <form onSubmit={send}>
                <div className="field-row">
                  <label className="field">
                    <span>Their email</span>
                    <input type="email" value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value, contact_id: null })} required disabled={busy} />
                  </label>
                  <label className="field">
                    <span>Their name (optional)</span>
                    <input type="text" value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })} disabled={busy} />
                  </label>
                </div>
                <label className="field">
                  <span>Note to them (optional)</span>
                  <input type="text" value={form.note} maxLength={1000} onChange={(e) => setForm({ ...form, note: e.target.value })} disabled={busy} />
                </label>
                <p className="toolbar">
                  <button type="submit" className="btn btn--primary btn--auto" disabled={busy || !form.to.trim()}>
                    {busy ? 'Sending…' : 'Send the request'}
                  </button>
                  <button type="button" className="btn btn--quiet btn--auto" onClick={() => setAsking(null)} disabled={busy}>
                    Cancel
                  </button>
                </p>
              </form>
            </section>
          )}

          <section className="panel" aria-labelledby="hist-heading">
            <h2 id="hist-heading">Every request</h2>
            {data.checks.length === 0 ? (
              <p className="muted">None yet.</p>
            ) : (
              <ul className="home__list">
                {data.checks.map((c) => (
                  <li key={c.id} className="home__item">
                    <div>
                      <strong>{c.agency_name}</strong> — asked {stamp(c.requested_at)} · to {c.sent_to_name ? `${c.sent_to_name} ` : ''}&lt;{c.sent_to}&gt;
                      <div className="muted">
                        <CheckStatus check={c} />
                        {c.responded_at ? ` · answered ${stamp(c.responded_at)}` : ''}
                        {c.note ? ` · your note: “${c.note}”` : ''}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
      {data === null && (
        <p>
          <Link to="/">Home</Link>
        </p>
      )}
    </>
  );
}

export default AffiliationsPage;
